import React from 'react';

export interface TabItem {
  id: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  variant?: 'pills' | 'underline' | 'segmented';
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  activeId,
  onChange,
  variant = 'pills',
  fullWidth = false,
  size = 'md',
  className = '',
}) => {
  const sizeClasses = {
    sm: 'text-xs py-1.5 px-3 min-h-[36px]',
    md: 'text-sm py-2 px-4 min-h-[40px]',
    lg: 'text-base py-2.5 px-5 min-h-[48px]',
  };

  return (
    <div
      role="tablist"
      className={`
        flex items-center gap-1.5 p-1 select-none overflow-x-auto custom-scrollbar
        ${variant === 'segmented' ? 'bg-slate-900/80 p-1 rounded-xl border border-white/5' : ''}
        ${variant === 'underline' ? 'border-b border-white/10 p-0 gap-4' : ''}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
    >
      {items.map((tab) => {
        const isActive = tab.id === activeId;

        let activeStyle = '';
        if (variant === 'pills') {
          activeStyle = isActive
            ? 'bg-accent-emerald text-slate-950 font-semibold shadow-[0_0_15px_rgba(16,185,129,0.35)]'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50';
        } else if (variant === 'segmented') {
          activeStyle = isActive
            ? 'bg-surface-hover text-accent-emerald font-semibold shadow-md border border-white/10'
            : 'text-text-secondary hover:text-text-primary hover:bg-white/5';
        } else if (variant === 'underline') {
          activeStyle = isActive
            ? 'text-accent-emerald font-semibold border-b-2 border-accent-emerald'
            : 'text-text-secondary hover:text-text-primary border-b-2 border-transparent';
        }

        return (
          <button
            key={tab.id}
            role="tab"
            type="button"
            aria-selected={isActive}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={`
              inline-flex items-center justify-center rounded-lg font-medium transition-all duration-150
              disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap
              ${sizeClasses[size]}
              ${fullWidth ? 'flex-1' : ''}
              ${activeStyle}
            `}
          >
            {tab.icon && <span className="mr-2 flex-shrink-0">{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.badge && <span className="ml-2 flex-shrink-0">{tab.badge}</span>}
          </button>
        );
      })}
    </div>
  );
};
